import {
  AGENCY_DBM_ID,
  AGENCY_DOH_ID,
  AGENCY_DPWH_ID,
  AGENCY_TESDA_ID,
} from "constants/agency";
import {
  ACCOUNT_HOUSE_SPEAKER,
  ACCOUNT_SUPER_ADMIN,
  canEditRoles,
} from "utility/constants";
import {
  ATC_ALLOCATION_TYPE_ID,
  withOtherInfo,
} from "constants/budgetAllocation";
import {
  ATCcongs,
  big3Congs,
  filteredAllocationTypes,
  houseSpeakerAllocationTypeOptions,
  nonAtcAllocationTypeOptions,
  withAllocationDetails,
} from "./constants";

export const validate = ({
  projectRecipients,
  selectedAgency,
  selectedProject,
  selectedCourses,
  uploadedFiles,
  role,
}) => {
  return new Promise((resolve, reject) => {
    const promises = [];

    projectRecipients?.forEach((recipient) => {
      const checkPromise = new Promise((resolveCheck) => {
        if (
          recipient.allocationType === 3 &&
          recipient.allocationContactNumber &&
          !/^(09)\d{9}$/.test(recipient.allocationContactNumber)
        ) {
          resolveCheck("Invalid contact number");
        }

        if (
          recipient.allocationType === 2 &&
          recipient.allocationContactNumber &&
          !/^(09)\d{9}$/.test(recipient.allocationContactNumber)
        ) {
          resolveCheck("Invalid atc partner contact");
        }

        if (
          recipient.allocationType === 3 &&
          (!recipient.allocationContactName ||
            !recipient.allocationContactNumber)
        ) {
          resolveCheck("Recipient Contact and Contact Number are required.");
        }

        if (recipient.allocationType === 2 && !recipient.allocationName) {
          resolveCheck("ATC is required");
        }

        if (
          selectedAgency?.value === AGENCY_TESDA_ID &&
          recipient.allocationType !== 2 &&
          (selectedProject?.value === 10 || selectedProject?.value === 15) &&
          !recipient?.tvi
        ) {
          resolveCheck("Name of TVI is required");
        }

        if (
          selectedAgency?.value === AGENCY_TESDA_ID &&
          recipient.allocationType !== 2 &&
          selectedCourses.length === 0
        ) {
          resolveCheck("At least select one TVI Course");
        }

        if (
          selectedAgency?.value === 14 &&
          recipient.allocationType !== 2 &&
          uploadedFiles.length === 0
        ) {
          resolveCheck("Supporting Documents is/are required");
        }

        if (
          selectedAgency?.value === 14 &&
          recipient.allocationType !== 2 &&
          recipient?.justification === ""
        ) {
          resolveCheck("Justification is required");
        }

        if (
          selectedAgency?.value === 14 &&
          recipient.allocationType !== 2 &&
          canEditRoles.includes(role) &&
          recipient?.fundSource === ""
        ) {
          resolveCheck("Fund Source is required");
        }

        if (
          selectedAgency?.value === 14 &&
          recipient.allocationType !== 2 &&
          recipient?.implementingOffice === ""
        ) {
          resolveCheck("Implementing Office is required");
        }

        if (
          selectedAgency?.value === 14 &&
          recipient.allocationType !== 2 &&
          !recipient?.institution
        ) {
          resolveCheck("Project Title is required");
        }

        if (
          selectedAgency?.value === 8 &&
          recipient.allocationType !== 2 &&
          !recipient.institution
        ) {
          resolveCheck("Facility is required");
        }

        if (
          selectedAgency?.value === 3 &&
          withOtherInfo.includes(recipient.allocationType) &&
          selectedProject?.value === 14 &&
          !recipient.institution
        ) {
          resolveCheck("Project Title is required");
        }

        if (
          selectedAgency?.value === 3 &&
          withOtherInfo.includes(recipient.allocationType) &&
          selectedProject?.value === 14 &&
          !recipient.otherInfo
        ) {
          resolveCheck("LGSF Tracking Code is required");
        }

        if (selectedAgency?.value === 27 && uploadedFiles.length === 0) {
          resolveCheck("Supporting documents cannot be empty");
        }

        resolveCheck(true);
      });

      promises.push(checkPromise);
    });

    Promise.all(promises)
      .then((results) => {
        const errors = results.filter((result) => result !== true);

        if (errors.length > 0) {
          resolve(errors.join("\n"));
        } else {
          resolve(true);
        }
      })
      .catch((error) => {
        reject(error);
      });
  });
};

export const getInstitutionParam = ({ agency, recipient, facilityType }) => {
  if (agency === 8) {
    if (recipient?.recipientName?.region?.value) {
      // eslint-disable-next-line max-len
      return `?ownership=${facilityType?.value}&type=Healthcare&regionId=${recipient?.recipientName?.region?.value}`;
    }

    return `?ownership=${facilityType?.value}&type=Healthcare`;
  }

  return "?type=Educational";
};

export const isOtherFormShown = (recipient) => recipient?.allocationType === 3;

export const isATCDropdownShown = (recipient) =>
  recipient?.allocationType === ATC_ALLOCATION_TYPE_ID;

export const isImplementingOfficeShown = (agency, recipient) =>
  agency === 14 && recipient?.allocationType !== ATC_ALLOCATION_TYPE_ID;

export const isTrackingCodeShown = (agency, selectedProject, recipient) =>
  agency === 3 &&
  selectedProject === 14 &&
  withOtherInfo.includes(recipient.allocationType);

export const isProjectTitleShown = (agency, selectedProject, recipient) =>
  (agency === 14 || (agency === 3 && selectedProject === 14)) &&
  ((agency === 3 && withOtherInfo.includes(recipient.allocationType)) ||
    (agency === 14 && recipient?.allocationType !== ATC_ALLOCATION_TYPE_ID));

const getInstitution = (i, selectedAgency, selectedProject) => {
  let institution = null;

  if (i.allocationType !== 2 && selectedAgency?.value !== 8) {
    if (
      selectedAgency?.value === AGENCY_TESDA_ID &&
      (selectedProject?.value === 10 || selectedProject?.value === 15)
    ) {
      institution = i?.institution?.value;
    }

    if (selectedAgency?.value === 14) {
      institution = i?.institution;
    }

    if (selectedAgency?.value === 3 && selectedProject?.value === 14) {
      institution = i?.institution;
    }
  } else if (selectedAgency?.value === 8) {
    institution = i?.institution?.value;
  } else if (
    selectedAgency?.value === 3 &&
    i.allocationType === 3 &&
    selectedProject?.value === 14
  ) {
    institution = i?.institution;
  }

  return institution;
};

export const getFormData = ({
  uploadedFiles,
  selectedAgency,
  selectedProject,
  projectId,
  projectRecipients,
  selectedCourses,
}) => {
  const files = uploadedFiles?.map((file) => file.location) || [];

  const data = {
    projectId,
    congressmen: projectRecipients.map((item) => ({
      id: item.recipientName.value,
      budget: item.budgetAllocation,
      agencyId: item.recipientName.region.value,
      allocationTypeId: item.allocationType,
      allocationName: item.allocationType === 2 ? item?.allocationName : null,
      allocationContactName: withAllocationDetails.includes(item.allocationType)
        ? item?.allocationContactName
        : null,
      allocationContactNumber: withAllocationDetails.includes(
        item.allocationType
      )
        ? item?.allocationContactNumber?.slice(1)
        : null,
      allocationReferenceKey:
        item.allocationType === ATC_ALLOCATION_TYPE_ID
          ? item?.allocationReferenceKey
          : null,
      allocation:
        item.allocationType === ATC_ALLOCATION_TYPE_ID
          ? {
              atcId: item?.allocation?.atcId,
              principalId: item?.allocation?.principalId,
              principal: {
                manager: {
                  firstName: item?.allocation?.managerFirstName,
                  lastName: item?.allocation?.managerLastName,
                  contact: item?.allocation?.managerContact,
                },
                partner: {
                  firstName: item?.allocation?.partnerFirstName,
                  lastName: item?.allocation?.partnerLastName,
                  contact: item?.allocation?.partnerContact,
                },
                cos: {
                  firstName: item?.allocation?.CosFirstName,
                  lastName: item?.allocation?.CosLastName,
                  contact: item?.allocation?.CosContact,
                },
              },
            }
          : null,
      documents: files ? { supporting: files } : null,
      provinceId:
        item?.Province?.value !== "All Provinces"
          ? item?.Province?.value
          : null,
      municipalityId:
        item?.Municipality?.value !== "All Municipalities"
          ? item?.Municipality?.value
          : null,
      regionId: item.recipientName?.region?.value || null,
      institution: getInstitution(item, selectedAgency, selectedProject),
      slots: item?.slots ? item?.slots : null,
      justification: item?.justification || null,
      ownership:
        selectedAgency?.value === AGENCY_DOH_ID &&
        typeof item?.institution?.value === "string"
          ? item?.facilityType?.value
          : null,
      fundSource:
        (selectedAgency?.value === AGENCY_DPWH_ID &&
          item?.allocationType !== ATC_ALLOCATION_TYPE_ID) ||
        (selectedAgency?.value === AGENCY_DBM_ID &&
          selectedProject?.value === AGENCY_DPWH_ID &&
          item?.allocationType !== ATC_ALLOCATION_TYPE_ID)
          ? item?.fundSource?.value || null
          : null,
      implementingOffice:
        selectedAgency?.value === AGENCY_DPWH_ID &&
        item.allocationType !== ATC_ALLOCATION_TYPE_ID
          ? item?.implementingOffice
          : null,
      otherInfo:
        selectedAgency?.value === AGENCY_DBM_ID &&
        selectedProject?.value === AGENCY_DPWH_ID &&
        withOtherInfo.includes(item?.allocationType)
          ? item?.otherInfo || null
          : null,
      editorNotes: item?.notes?.map((note) => note?.value?.trim()).join(";"),
      officialId: item.officialId,
      officialPosition: item.officialPosition,
      tviId:
        selectedAgency?.value === AGENCY_TESDA_ID &&
        item.allocationType !== ATC_ALLOCATION_TYPE_ID
          ? item?.tvi
          : null,
      courses:
        selectedAgency?.value === AGENCY_TESDA_ID &&
        item.allocationType !== ATC_ALLOCATION_TYPE_ID
          ? selectedCourses?.map((course) => ({
              slots: course.slots,
              tviCourseId: course.id,
            })) || []
          : null,
      tviEndorsedVia:
        selectedAgency?.value === AGENCY_TESDA_ID ? "addProgram" : null,
      isRequestList: item?.isRequestList ? 1 : 0,
      ...(item?.isRequestList && {
        passCodeValidUntil: item?.passcodeValidity,
      }),
    })),
  };

  return data;
};

export const validateOthersForm = (form) => {
  if (
    form.contact?.length > 0 &&
    (form.contact?.length <= 2 || !/^(09)\d{9}$/.test(form.contact))
  ) {
    return "Invalid Others contact number.";
  }

  if (form.lastName === "") {
    return "Required Last Name.";
  }

  if (form.firstName === "") {
    return "Required First Name.";
  }

  return true;
};

export const getAllocationTypeOptions = ({
  role,
  congressman,
  filterMode = false,
}) => {
  if (role === ACCOUNT_HOUSE_SPEAKER) return houseSpeakerAllocationTypeOptions;

  if (filterMode && role === ACCOUNT_SUPER_ADMIN) {
    return filteredAllocationTypes;
  }

  if (
    role === ACCOUNT_SUPER_ADMIN &&
    congressman &&
    big3Congs.includes(congressman)
  ) {
    return filteredAllocationTypes;
  }

  if (congressman && ATCcongs.includes(congressman)) {
    return houseSpeakerAllocationTypeOptions;
  }

  return nonAtcAllocationTypeOptions;
};

export const isAllocationTypeDisabled = ({ role, congressman }) => {
  if (role === ACCOUNT_HOUSE_SPEAKER) return false;

  if (congressman && ATCcongs.includes(congressman)) {
    return false;
  }

  return true;
};
